import React, { useState, useEffect, useCallback } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { Github, Linkedin, Mail, Sun, Moon } from 'lucide-react';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js';
import { Radar } from 'react-chartjs-2';
import Slider from 'react-slick';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';

// Register Chart.js components
ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// --- Custom InView Hook (for scroll animations) ---
function useInView(options) {
  const ref = React.useRef();
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        if (options && options.once) {
          observer.disconnect();
        }
      } else if (!options || !options.once) {
        setInView(false);
      }
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [options]);

  return [ref, inView];
}


export default function Portfolio() {
  const [projectMode, setProjectMode] = useState({});
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'dark';
    }
    return 'dark';
  });

  const toggleMode = (id) => {
    setProjectMode((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.add('light');
      document.body.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'dark' ? 'light' : 'dark'));
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  // --- Projects Data from the latest resume ---
  const projects = [
    {
      id: 'agromitra',
      title: "AgroMitra ML",
      stack: "Node.js/Express, Flask, MongoDB, Scikit-Learn, Gemini API",
      overview: "Microservices-powered agricultural assistant with weather prediction and crop guidance.",
      impact: "37+ REST APIs and zero-downtime ML support",
      githubUrl: "https://github.com/hmm183/agromitra-ml",
      liveUrl: "https://agromitra-ml.vercel.app",
      details:
        "Led backend and ML development for an agri-tech platform with 37 RESTful endpoints spanning authentication, crop marketplace, soil diagnostics, weather advisories, payments, and admin workflows, backed by secure Google OAuth and Razorpay integrations. Built a Flask-based Random Forest service for weather prediction and crop/fertilizer recommendations, plus a TF-IDF subsidy-matching assistant with Gemini API fallback for zero-downtime support.",
    },
    {
      id: 'gradecalc',
      title: "VIT-AP Grade Calculator",
      stack: "React.js, Node.js/Express, PBKDF2",
      overview: "Scalable grade tracking platform used by 600+ active users.",
      impact: "600+ active users and secure admin workflows",
      githubUrl: "https://github.com/hmm183/studentgradecalculator",
      liveUrl: "https://vitapgradecalculator.vercel.app",
      details:
        "Delivered a full-stack grade tracking and course registration platform that scaled to 600+ active users. Automated the relative-grading results parser, secured admin endpoints with PBKDF2 authentication and rate-limiting, and streamlined faculty mapping workflows.",
    },
    {
      id: 'vittyllm',
      title: "Vitty LLM",
      stack: "Llama, GGUF Quantization, Ollama",
      overview: "Fine-tuned LLM deployment pipeline for edge devices.",
      impact: "Optimized deployment for resource-constrained hardware",
      liveUrl: "https://ollama.com/vrishankraina/vittyquantized",
      details:
        "Built an end-to-end fine-tuning workflow for a specialized Llama-based persona model and optimized edge deployment by resolving vocabulary-size mismatches during GGUF quantization, enabling efficient deployment on resource-constrained devices.",
    },
    {
      id: 'swiftmedilink',
      title: "SwiftMediLink",
      stack: "FHIR, CNN, OCR, RBAC",
      overview: "OCR-driven healthcare platform with secure multi-role access.",
      impact: "85–95% inference accuracy on 10,000+ medical samples",
      githubUrl: "https://github.com/hmm183/sih",
      liveUrl: "https://swiftmedilink.vercel.app",
      details:
        "Implemented OCR-based prescription digitization and authentication/RBAC across multiple user roles for a FHIR-compliant EHR platform. Deployed CNN-based diagnostic models validated at 85–95% inference accuracy on 10,000+ labeled samples into a production inference pipeline with end-to-end encryption.",
    },
  ];

  const experiences = [
    {
      role: "Full-Stack Developer Intern",
      company: "Purple Technologies",
      period: "Jan 2025 - Mar 2025",
      location: "Andhra Pradesh",
      description:
        "Built a role-based full-stack Node.js and Express application for College Connect with JWT authentication and modular middleware, enabling dynamic CRUD operations for college events and clubs. Developed MongoDB-backed RESTful services to support real-time student interactions and scalable multi-user workflows.",
    },
  ];

  const education = [
    {
      degree: "B.Tech in Computer Science & Engineering (Core)",
      institution: "VIT-AP University, Amaravati",
      period: "Expected 2027",
      details: "CGPA: 9.09/10 and top 20% of the branch.",
    },
    {
      degree: "PCMCE",
      institution: "Vydehi School of Excellence, Bengaluru",
      period: "2023",
      details: "Scored 85.6% and earned a Gold accolade at the Model United Nations conference.",
    },
  ];

  const patentsPublications = [
    {
      title: "Neuvay",
      type: "📜 Published Patent",
      details: "Co-inventor on a decentralized cross-border fiat-to-fiat P2P payment platform.",
    },
    {
      title: "MediLink",
      type: "📜 Published Patent",
      details: "Co-inventor on a real-time healthcare system with drift-aware TinyML architecture and continual learning.",
    },
    {
      title: "Electroactive Polymers and Polymer Nanocomposites for Cooling Technology",
      type: "📜 RSC Publication",
      details: "Contributing author on a publication now in press with RSC Publications.",
    },
  ];

  const openSourceContributions = [
    {
      title: "FreeLLMAPI",
      role: "Contributor",
      details: "Improved the /v1/models SQLite query with an EXISTS clause to eliminate dead-end routing and reduce payload size.",
      prUrl: "https://github.com/tashfeenahmed/freellmapi/commit/2180eadf7a3ddaba2931c0c494e4a5d73766db54",
    },
    {
      title: "GDG Backend",
      role: "Contributor",
      details: "Engineered admin/public REST APIs with Prisma ORM, RBAC, and Zod validation for a secure data layer.",
    },
  ];

  const leadershipAwards = [
    {
      title: "Vice President, Android Club",
      role: "Leadership",
      details: "Led community initiatives and helped strengthen student engagement in technology events.",
    },
    {
      title: "Top 50 Internal, Smart India Hackathon",
      role: "Award",
      details: "Reached the top 50 shortlist in a competitive innovation challenge.",
    },
    {
      title: "Top 3 of 150+ teams, TechNov 2024",
      role: "Award",
      details: "Led a standout performance that earned a Purple Technologies internship offer.",
    },
  ];

  const certifications = [
    {
      title: "AWS Academy Graduate",
      issuer: "AWS Academy",
      period: "2025",
      details:
        "Completed cloud architecting and foundations coursework with hands-on exposure to cloud infrastructure concepts.",
    },
    {
      title: "OCI 2025 Certified Foundations Associate",
      issuer: "Oracle",
      period: "2025",
      details:
        "Earned foundational certification in OCI cloud services, architecture, and core cloud principles.",
    },
    {
      title: "Blackbucks Cloud Computing Credit Course",
      issuer: "VIT-AP",
      period: "2025",
      details:
        "Completed a focused cloud computing course covering practical deployment concepts and containerization workflows.",
    },
  ];

  // Skills Data for Radar Chart (with proficiency)
  const skillProficiency = {
    "Python": 4,
    "Java": 5,
    "JavaScript": 5,
    "React.js": 5,
    "Node.js": 5,
    "Express.js": 4,
    "Flask": 4,
    "REST APIs": 5,
    "JWT/OAuth": 4,
    "MongoDB": 5,
    "PostgreSQL": 4,
    "Docker": 1,
    "AWS": 4,
    "OCI": 4,
    "LLMs": 5,
    "LangChain": 4,
    "OpenCV": 4,
    "Whisper": 5,
  };

  const radarChartData = {
    labels: Object.keys(skillProficiency),
    datasets: [
      {
        label: 'Proficiency (1-5)',
        data: Object.values(skillProficiency),
        backgroundColor: 'rgba(54, 162, 235, 0.2)', // Blue
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
        pointBackgroundColor: 'rgba(54, 162, 235, 1)',
        pointBorderColor: '#181818',
        pointHoverBackgroundColor: '#333',
        pointHoverBorderColor: 'rgba(54, 162, 235, 1)',
      },
    ],
  };

  const radarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: {
          color: theme === 'dark' ? 'rgba(255, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        },
        grid: {
          color: theme === 'dark' ? 'rgba(255, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        },
        pointLabels: {
          color: theme === 'dark' ? '#333' : '#333',
          font: {
            size: 12,
          },
        },
        ticks: {
          beginAtZero: true,
          max: 5,
          stepSize: 1,
          color: theme === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.7)',
          backdropColor: 'transparent',
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: theme === 'dark' ? '#333' : '#333',
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.r !== null) {
              label += context.parsed.r;
            }
            return label;
          }
        }
      },
    },
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const particlesInit = useCallback(async engine => {
    await loadSlim(engine);
  }, []);

  const particlesLoaded = useCallback(async container => {
    // console.log(container);
  }, []);

  const particlesOptions = {
    background: {
      color: {
        value: "transparent",
      },
    },
    fpsLimit: 60,
    interactivity: {
      events: {
        onClick: {
          enable: true,
          mode: "push",
        },
        onHover: {
          enable: true,
          mode: "repulse",
        },
        resize: true,
      },
      modes: {
        push: {
          quantity: 4,
        },
        repulse: {
          distance: 100,
          duration: 0.4,
        },
      },
    },
    particles: {
      color: {
        value: "#888",
      },
      links: {
        color: "#888",
        distance: 150,
        enable: true,
        opacity: 0.3,
        width: 1,
      },
      collisions: {
        enable: true,
      },
      move: {
        direction: "none",
        enable: true,
        outModes: {
          default: "bounce",
        },
        random: false,
        speed: 1,
        straight: false,
      },
      number: {
        density: {
          enable: true,
          area: 800,
        },
        value: 50,
      },
      opacity: {
        value: 0.5,
      },
      shape: {
        type: "circle",
      },
      size: {
        value: { min: 1, max: 3 },
      },
    },
    detectRetina: true,
  };


  return (
    <div className={`bg-gradient-to-br min-h-screen font-sans ${theme === 'dark' ? 'from-gray-950 to-black text-white' : 'from-gray-100 to-white text-gray-800'}`}>
      <Particles
        id="tsparticles"
        init={particlesInit}
        loaded={particlesLoaded}
        options={particlesOptions}
        className="absolute inset-0 z-0"
      />

      <div className="relative z-10 p-6 md:p-12 max-w-5xl mx-auto">
        <button
          onClick={toggleTheme}
          className="fixed top-4 right-4 p-3 rounded-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-800 shadow-lg z-50 hover:scale-110 transition-transform duration-200"
          aria-label="Toggle dark mode"
        >
          {theme === 'dark' ? <Sun size={24} /> : <Moon size={24} />}
        </button>

        {/* Hero Section */}
        <motion.section
          className="text-center mb-16"
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-extrabold tracking-tight bg-clip-text bg-gradient-to-r from-blue-400 to-purple-600 text-transparent"
          >
            Vrishank Raina
          </motion.h1>
          <p className="mt-4 text-lg md:text-xl text-gray-300 dark:text-gray-600 leading-relaxed">
            Full-Stack Developer and AI/ML Engineer passionate about building scalable backend systems, intelligent applications, and production-ready AI solutions.        </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm font-semibold">
          </div>
          <div className="flex justify-center gap-6 mt-6">
            <a
              href="mailto:vrishankraina@gmail.com"
              className="text-gray-400 hover:text-blue-400 dark:hover:text-blue-600 transition-transform duration-300 transform hover:scale-110"
            >
              <Mail size={28} />
            </a>
            <a
              href="https://github.com/hmm183"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-blue-400 dark:hover:text-blue-600 transition-transform duration-300 transform hover:scale-110"
            >
              <Github size={28} />
            </a>
            <a
              href="https://linkedin.com/in/vrishank-raina"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-blue-400 dark:hover:text-blue-600 transition-transform duration-300 transform hover:scale-110"
            >
              <Linkedin size={28} />
            </a>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button
              className="px-6 py-3 border border-blue-500 text-blue-300 rounded-lg shadow-lg hover:bg-blue-600 hover:text-white transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 font-semibold text-lg"
              onClick={() => window.open('/resume.pdf')}
            >
              Download Resume
            </button>
            <a
              href="https://github.com/hmm183"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 border border-gray-400 text-gray-200 rounded-lg shadow-lg hover:bg-gray-700 hover:text-white transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 font-semibold text-lg"
            >
              GitHub
            </a>
          </div>
        </motion.section>

        {/* Experience Section - Added hover interactivity */}
        <motion.section
          className="mb-16"
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-blue-300 dark:text-blue-600 mb-8 text-center">Experience</h2>
          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <div key={index} className="bg-gray-900 border border-gray-700 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-800 rounded-xl p-6 shadow-xl
                hover:shadow-2xl hover:-translate-y-1 transform transition-all duration-300 ease-in-out cursor-pointer"> {/* Added hover effects */}
                <h3 className="text-2xl font-bold">{exp.role}</h3>
                <p className="text-sm text-gray-400 dark:text-gray-600 italic">
                  {exp.company} | {exp.period} | {exp.location}
                </p>
                <p className="mt-4 text-gray-200 dark:text-gray-700">{exp.description}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Projects Section - Carousel, card interactivity already there */}
        <motion.section
          className="mb-16"
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-blue-300 dark:text-blue-600 mb-8 text-center">Projects</h2>
          <Slider {...sliderSettings}>
            {projects.map((proj) => (
              <div key={proj.id} className="p-4">
                <div
                  className="bg-gray-900 border border-gray-700 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-800 rounded-xl p-6 shadow-xl min-h-[300px] flex flex-col justify-between
                  hover:shadow-2xl hover:-translate-y-1 transform transition-all duration-300 ease-in-out cursor-pointer" // Added hover effects to cards
                >
                  <div>
                    <a
                      href={proj.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-2xl font-bold mb-2 block text-blue-300 hover:text-blue-200 dark:text-blue-700 dark:hover:text-blue-600 transition-colors"
                    >
                      {proj.title}
                    </a>
                    <p className="text-sm text-gray-400 dark:text-gray-600 italic mb-3">{proj.stack}</p>
                    <p className="mb-3 rounded-full border border-blue-400/30 bg-blue-500/10 px-3 py-1 text-sm text-blue-300 dark:text-blue-700 inline-block">{proj.impact}</p>
                    <p className="text-gray-200 dark:text-gray-700 leading-relaxed">
                      {projectMode[proj.id] ? proj.details : proj.overview}
                    </p>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      className="rounded-lg border border-blue-500/40 bg-blue-500/10 px-3 py-2 text-sm font-semibold text-blue-400 transition-all duration-200 hover:bg-blue-500/20 hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                      onClick={() => toggleMode(proj.id)}
                    >
                      {projectMode[proj.id] ? "Less Info" : "More Info"}
                    </button>
                    {proj.githubUrl && (
                      <a
                        href={proj.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-lg border border-gray-500/40 bg-gray-800/70 px-3 py-2 text-sm font-semibold text-gray-200 transition-all duration-200 hover:bg-gray-700 hover:text-white"
                      >
                        GitHub
                      </a>
                    )}
                    {proj.liveUrl && (
                      <a
                        href={proj.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-lg border border-purple-500/40 bg-purple-500/10 px-3 py-2 text-sm font-semibold text-purple-300 transition-all duration-200 hover:bg-purple-500/20 hover:text-purple-200"
                      >
                        Live Demo
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </motion.section>

        {/* Education Section - Added hover interactivity */}
        <motion.section
          className="mb-16"
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-green-300 dark:text-green-600 mb-8 text-center">Education</h2>
          <div className="space-y-8">
            {education.map((edu, index) => (
              <div key={index} className="bg-gray-900 border border-gray-700 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-800 rounded-xl p-6 shadow-xl
                hover:shadow-2xl hover:-translate-y-1 transform transition-all duration-300 ease-in-out cursor-pointer"> {/* Added hover effects */}
                <h3 className="text-2xl font-bold">{edu.degree}</h3>
                <p className="text-sm text-gray-400 dark:text-gray-600 italic">
                  {edu.institution} | {edu.period}
                </p>
                <p className="mt-4 text-gray-200 dark:text-gray-700">{edu.details}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Patents & Publications */}
        <motion.section
          className="mb-16"
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-purple-300 dark:text-purple-600 mb-8 text-center">Patents & Publications</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {patentsPublications.map((item, index) => (
              <div key={index} className="bg-gray-900 border border-gray-700 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-800 rounded-xl p-6 shadow-xl hover:shadow-2xl hover:-translate-y-1 transform transition-all duration-300 ease-in-out">
                <p className="inline-flex rounded-full border border-purple-400/40 bg-purple-500/10 px-3 py-1 text-sm font-semibold uppercase tracking-wide text-purple-300 dark:text-purple-700">{item.type}</p>
                <h3 className="text-xl font-bold mt-2">{item.title}</h3>
                <p className="mt-4 text-gray-200 dark:text-gray-700">{item.details}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Open Source Contributions */}
        <motion.section
          className="mb-16"
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-blue-300 dark:text-blue-600 mb-8 text-center">Open Source Contributions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {openSourceContributions.map((item, index) => (
              <div key={index} className="bg-gray-900 border border-gray-700 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-800 rounded-xl p-6 shadow-xl hover:shadow-2xl hover:-translate-y-1 transform transition-all duration-300 ease-in-out">
                <p className="text-sm text-blue-300 dark:text-blue-700 font-semibold uppercase tracking-wide">{item.role}</p>
                <h3 className="text-xl font-bold mt-2">{item.title}</h3>
                <p className="mt-4 text-gray-200 dark:text-gray-700">{item.details}</p>
                {item.prUrl && (
                  <a
                    href={item.prUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex rounded-lg border border-green-500/40 bg-green-500/10 px-3 py-2 text-sm font-semibold text-green-300 transition-all duration-200 hover:bg-green-500/20 hover:text-green-200"
                  >
                    View PR
                  </a>
                )}
              </div>
            ))}
          </div>
        </motion.section>

        {/* Leadership & Awards */}
        <motion.section
          className="mb-16"
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-yellow-300 dark:text-yellow-600 mb-8 text-center">Leadership & Awards</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {leadershipAwards.map((item, index) => (
              <div key={index} className="bg-gray-900 border border-gray-700 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-800 rounded-xl p-6 shadow-xl hover:shadow-2xl hover:-translate-y-1 transform transition-all duration-300 ease-in-out">
                <p className="text-sm text-yellow-300 dark:text-yellow-700 font-semibold uppercase tracking-wide">{item.role}</p>
                <h3 className="text-xl font-bold mt-2">{item.title}</h3>
                <p className="mt-4 text-gray-200 dark:text-gray-700">{item.details}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Certifications Section - Added hover interactivity */}
        <motion.section
          className="mb-16"
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-yellow-300 dark:text-yellow-600 mb-8 text-center">Certifications</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-gray-900 border border-gray-700 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-800 rounded-xl p-6 shadow-xl min-h-[220px] flex flex-col justify-between
                hover:shadow-2xl hover:-translate-y-1 transform transition-all duration-300 ease-in-out cursor-pointer"> {/* Added hover effects */}
                <div>
                  <h3 className="text-xl font-bold">{cert.title}</h3>
                  <p className="text-sm text-gray-400 dark:text-gray-600 italic">
                    {cert.issuer} | {cert.period}
                  </p>
                  <p className="mt-4 text-gray-200 dark:text-gray-700">{cert.details}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Skills Section - Radar Chart and Hover for Skill Boxes */}
        <motion.section
          className="mb-16"
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-green-300 dark:text-green-600 text-center mb-8">Skills</h2>
          <div className="bg-gray-900 border border-gray-700 dark:bg-gray-100 dark:border-gray-300 p-6 rounded-xl shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-center text-white dark:text-gray-800">Skill Proficiency Radar</h3>
            <div className="chart-container">
              <Radar data={radarChartData} options={radarChartOptions} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-300 dark:text-gray-700 mt-6">
              {Object.keys(skillProficiency).map((skill, index) => (
                <span key={index} className="skill-item p-2 bg-gray-800 dark:bg-gray-200 dark:text-gray-700 rounded-md text-center
                  hover:scale-105 hover:bg-blue-600 hover:text-white dark:hover:bg-blue-400 dark:hover:text-gray-900 transform transition-all duration-200 ease-in-out cursor-default"> {/* Added hover effects to skill boxes */}
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Footer */}
        <motion.footer
          className="mt-20 text-center text-gray-500 dark:text-gray-400 text-sm py-4 border-t border-gray-800 dark:border-gray-300"
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          Built by Vrishank Raina — 2026
        </motion.footer>
      </div>
    </div>
  );
}
